import { useState, useCallback } from 'react';
import { useApi } from '../../../shared/hooks/useApi';
import { friendshipAPI, type Friendship, type SendFriendRequestCommand } from '../../../services/friendshipApi';

export function useFriendRequests() {
  const [pendingRequests, setPendingRequests] = useState<Friendship[]>([]);
  const [sentRequests, setSentRequests] = useState<Friendship[]>([]);
  
  const {
    execute: fetchPendingRequests,
    isLoading: isLoadingPending,
    error: pendingError
  } = useApi<Friendship[]>(friendshipAPI.getPendingRequests);
  
  const {
    execute: fetchSentRequests,
    isLoading: isLoadingSent,
    error: sentError
  } = useApi<Friendship[]>(friendshipAPI.getSentRequests);
  
  const {
    execute: sendRequest,
    isLoading: isSending,
    error: sendError
  } = useApi<Friendship>(friendshipAPI.sendFriendRequest);
  
  const {
    execute: acceptRequest,
    isLoading: isAccepting,
    error: acceptError
  } = useApi<Friendship>(friendshipAPI.acceptFriendRequest);
  
  const {
    execute: rejectRequest,
    isLoading: isRejecting,
    error: rejectError
  } = useApi<Friendship>(friendshipAPI.rejectFriendRequest);
  
  const loadPendingRequests = useCallback(async () => {
    const result = await fetchPendingRequests();
    if (result) {
      setPendingRequests(result);
    }
    return result;
  }, [fetchPendingRequests]);
  
  const loadSentRequests = useCallback(async () => {
    const result = await fetchSentRequests();
    if (result) {
      setSentRequests(result);
    }
    return result;
  }, [fetchSentRequests]);
  
  const sendFriendRequest = useCallback(async (command: SendFriendRequestCommand) => {
    const result = await sendRequest(command);
    if (result) {
      // Refresh sent requests to show the new request
      await loadSentRequests();
    }
    return result;
  }, [sendRequest, loadSentRequests]);
  
  const acceptFriendRequest = useCallback(async (friendshipId: string) => {
    const result = await acceptRequest(friendshipId);
    if (result) {
      // Remove from pending requests
      setPendingRequests(prev => prev.filter(req => req.id !== friendshipId));
    }
    return result;
  }, [acceptRequest]);
  
  const rejectFriendRequest = useCallback(async (friendshipId: string) => {
    const result = await rejectRequest(friendshipId);
    if (result) {
      // Remove from pending requests
      setPendingRequests(prev => prev.filter(req => req.id !== friendshipId));
    }
    return result;
  }, [rejectRequest]);
  
  const loadAllRequests = useCallback(async () => {
    await Promise.all([
      loadPendingRequests(),
      loadSentRequests()
    ]);
  }, [loadPendingRequests, loadSentRequests]);
  
  return {
    pendingRequests,
    sentRequests,
    isLoading: isLoadingPending || isLoadingSent || isSending || isAccepting || isRejecting,
    error: pendingError || sentError || sendError || acceptError || rejectError,
    loadPendingRequests,
    loadSentRequests,
    loadAllRequests,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest
  };
}